import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Globe, { type GlobeHandle } from "./components/Globe";
import Chrome from "./components/Chrome";
import HoverCard from "./components/HoverCard";
import CountryPanel from "./components/CountryPanel";
import AudioToggle from "./components/AudioToggle";
import type { CountryFeature, HoverPosition } from "./types";
import "./App.css";

function useWindowSize() {
  const [size, setSize] = useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });
  useEffect(() => {
    const onResize = () =>
      setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

export default function App() {
  const { w, h } = useWindowSize();
  const globeRef = useRef<GlobeHandle>(null);
  const [hovered, setHovered] = useState<CountryFeature | null>(null);
  const [hoverPos, setHoverPos] = useState<HoverPosition | null>(null);
  const [selected, setSelected] = useState<CountryFeature | null>(null);
  const [centroid, setCentroid] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [countryCount, setCountryCount] = useState(0);
  const [booted, setBooted] = useState(false);

  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!ready) return;
    const t1 = setTimeout(() => setBooted(true), 900);
    const t2 = setTimeout(() => globeRef.current?.playEntrance(), 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [ready]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      setHoverPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const onHover = useCallback((c: CountryFeature | null) => {
    setHovered(c);
  }, []);

  const onSelect = useCallback((c: CountryFeature | null) => {
    setSelected(c);
    if (c) {
      const geom = c.geometry;
      try {
        const flat: number[][] = [];
        const walk = (arr: unknown): void => {
          if (!Array.isArray(arr)) return;
          if (typeof arr[0] === "number") {
            flat.push(arr as number[]);
          } else {
            (arr as unknown[]).forEach(walk);
          }
        };
        walk(geom.coordinates);
        if (flat.length) {
          const lngs = flat.map((p) => p[0]);
          const lats = flat.map((p) => p[1]);
          const lng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
          const lat = (Math.min(...lats) + Math.max(...lats)) / 2;
          setCentroid({ lat, lng });
          // when panel takes up the right side, aim camera a bit east of centroid
          globeRef.current?.flyTo(lat, lng - 18, 1.6);
          globeRef.current?.setAutoRotate(false);
        }
      } catch {
        /* ignore */
      }
    } else {
      setCentroid(null);
      globeRef.current?.setAutoRotate(true);
    }
  }, []);

  return (
    <div className={`app ${selected ? "has-selection" : ""}`}>
      <div className="grid-bg" aria-hidden />
      <div className="vignette" aria-hidden />

      <AnimatePresence>
        {!booted ? (
          <motion.div
            key="boot"
            className="boot"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="boot-inner">
              <span className="boot-dot" />
              initializing
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className={`globe-stage ${selected ? "shifted" : ""}`}>
        <Globe
          ref={globeRef}
          width={w}
          height={h}
          selected={selected}
          onHover={onHover}
          onSelect={onSelect}
          onLoaded={setCountryCount}
          onReady={() => setReady(true)}
        />
      </div>

      <Chrome countryCount={countryCount} />
      <AudioToggle />

      <HoverCard
        feature={hovered && !selected ? hovered : null}
        position={hoverPos}
      />

      <CountryPanel
        feature={selected}
        centroid={centroid}
        onClose={() => onSelect(null)}
      />
    </div>
  );
}
